import {Action, openDeleteDialog} from 'platform/components';

import {matchPath, useLocation} from 'react-router-dom';

import {isNil} from 'ramda';
import {isNotNil} from 'ramda-adjunct';

import {useChangeRentVehicleInspectionMutation, useGetRentalOrderQuery} from '@dms/api/rental';
import i18n from '@dms/i18n';
import {rentalOrderRoutes} from '@dms/routes';
import {handleApiError} from '@dms/shared';
import {NewInspectionButton} from '@dms/teas';

import {buildArray, composePath, isNotNilOrEmpty, useRequiredParams} from 'shared/web';

export const useRentInspectionActions = () => {
  const {id: rentalOrderId} = useRequiredParams();
  const {data: rentalOrder} = useGetRentalOrderQuery({rentalOrderId});
  const [updateRentInspection] = useChangeRentVehicleInspectionMutation();
  const {pathname} = useLocation();

  const vehicleId = rentalOrder?.rentalVehicleId;
  const hasRentInspection = isNotNilOrEmpty(rentalOrder?.rentVehicleInspections);

  const inspectionPath = composePath(rentalOrderRoutes.rentVehicleInspections, {
    params: {id: rentalOrderId},
  });

  const shouldShowNewInspectionButton = isNotNil(matchPath(pathname, inspectionPath.split('?')[0]));

  const handleUpdateRentInspection = async (inspectionId: string) => {
    await updateRentInspection({
      rentalOrderId,
      changeRentVehicleInspectionRequestBody: {
        inspections: [inspectionId],
      },
    })
      .unwrap()
      .catch(handleApiError);
  };

  const handleRemoveRentInspection = () => {
    openDeleteDialog({
      text: i18n.t('page.rentalOrder.labels.removeInspectionDescription'),
      onConfirm: async () =>
        await updateRentInspection({
          rentalOrderId,
          changeRentVehicleInspectionRequestBody: {
            inspections: [],
          },
        })
          .unwrap()
          .catch(handleApiError),
    });
  };

  if (isNil(rentalOrderId) || isNil(vehicleId) || !shouldShowNewInspectionButton) {
    return [];
  }

  return buildArray<Action>()
    .when(hasRentInspection, {
      type: 'button',
      title: i18n.t('page.rentalOrder.labels.removeInspection'),
      onClick: handleRemoveRentInspection,
      variant: 'danger',
    })
    .whenNot(hasRentInspection, {
      type: 'custom',
      component: (
        <NewInspectionButton
          wrapperBoxProps={{paddingBottom: 0}}
          vehicleId={vehicleId ?? ''}
          onAfterSubmit={handleUpdateRentInspection}
          getRedirectRoute={() =>
            composePath(rentalOrderRoutes.rentVehicleInspections, {
              params: {
                id: rentalOrder?.rentalOrderId,
              },
            })
          }
        />
      ),
    });
};
